import type { CSSProperties } from 'react';

/**
 * Appendix page tokens -- the single source of truth for the geometry and type
 * of every appendix page -- the photo grid, the table and the location plan --
 * shared by the editor pages and DocumentPDF (parity rule 1 in DEVELOPMENT.md).
 * The table is paginated on MEASURED heights, so every size the editor draws
 * must be the size the PDF draws, to the point; the photo grid is laid out by
 * photoAppendixLayout from these numbers on both surfaces. Hence points
 * throughout -- react-pdf's unit, and one CSS understands natively, so the
 * same number is written into both surfaces.
 *
 * The title is the photo appendix's original on-screen look (16px / 600, 2px
 * padding inside a 1px frame, 8px below) expressed in points, and every
 * appendix's title now uses it.
 */
export const APPENDIX_TOKENS = {
  // --- page geometry (mm, the same box the photo appendix uses) ---
  padTopMm: 15,
  padBottomMm: 30,
  padSideMm: 20,
  /** 210 - 2 x 20 */
  contentWidthMm: 170,
  /** 297 - 15 - 30 */
  contentHeightMm: 252,

  // --- title ---
  titleSizePt: 12,
  titleLineHeight: 1.15,
  /** Vertical padding + frame inside the title box, per side. 2px + 1px. */
  titlePadPt: 2.25,
  /** Horizontal padding inside the title box. 4px + 1px. */
  titlePadXPt: 3.75,
  /** Space under the title. 8px. */
  titleGapPt: 6,

  // --- table ---
  headSizePt: 9,
  cellSizePt: 10,
  lineHeight: 1.15,
  /** Vertical padding of a cell, per side. */
  cellPadPt: 3,
  /** Horizontal padding of a cell, per side. */
  cellPadXPt: 4,
  /**
   * 0.75pt, not 0.5pt: Chrome resolves a sub-pixel border to 0.5px (0.375pt)
   * while react-pdf honours the value it is given, and the editor is what
   * paginates. 0.75pt is exactly 1px on both. See siChromeTokens.
   */
  ruleWidthPt: 0.75,
  headRule: '#374151',
  rowRule: '#e5e7eb',
  zebra: '#f3f4f6',
  headInk: '#111827',
  cellInk: '#111827',
  sectionInk: '#111827',

  // --- photo grid ---
  /** Between columns and between rows. 8px on screen. */
  photoGapPt: 6,
  /** Above the "Photo n: caption" line. 4px on screen. */
  photoLabelGapPt: 3,
  photoLabelSizePt: 9,
  photoLabelLineHeight: 1.3,
  /** Caption lines every cell leaves room for under its photo. */
  photoLabelLines: 2,

  // --- plan markers (mm, because they sit on a box measured in mm) ---
  markerRadiusMm: 3,
  /** From the circle's edge to the arrow's tip. */
  arrowLengthMm: 4,
  arrowHalfWidthMm: 2,
  markerStrokeMm: 0.35,
  // The brand navy ($primary-dark in sass/_variables.scss), restated here
  // because SCSS cannot import this module; change both together.
  markerFill: '#ffffff',
  markerStroke: '#1a2e5b',
  markerInk: '#1a2e5b',
  markerLabelSizePt: 8,
} as const;

/**
 * The tokens as CSS custom properties, applied to the editor root so the
 * appendix page stylesheets consume them without a second copy of the values.
 */
export const APPENDIX_CSS_VARS = {
  '--apx-pad-top': `${APPENDIX_TOKENS.padTopMm}mm`,
  '--apx-pad-bottom': `${APPENDIX_TOKENS.padBottomMm}mm`,
  '--apx-pad-side': `${APPENDIX_TOKENS.padSideMm}mm`,
  '--apx-title-size': `${APPENDIX_TOKENS.titleSizePt}pt`,
  '--apx-title-line': `${APPENDIX_TOKENS.titleLineHeight}`,
  '--apx-title-pad': `${APPENDIX_TOKENS.titlePadPt}pt`,
  '--apx-title-pad-x': `${APPENDIX_TOKENS.titlePadXPt}pt`,
  '--apx-title-gap': `${APPENDIX_TOKENS.titleGapPt}pt`,
  '--apx-head-size': `${APPENDIX_TOKENS.headSizePt}pt`,
  '--apx-cell-size': `${APPENDIX_TOKENS.cellSizePt}pt`,
  '--apx-line': `${APPENDIX_TOKENS.lineHeight}`,
  '--apx-cell-pad': `${APPENDIX_TOKENS.cellPadPt}pt`,
  '--apx-cell-pad-x': `${APPENDIX_TOKENS.cellPadXPt}pt`,
  '--apx-rule-width': `${APPENDIX_TOKENS.ruleWidthPt}pt`,
  '--apx-head-rule': APPENDIX_TOKENS.headRule,
  '--apx-row-rule': APPENDIX_TOKENS.rowRule,
  '--apx-zebra': APPENDIX_TOKENS.zebra,
  '--apx-head-ink': APPENDIX_TOKENS.headInk,
  '--apx-cell-ink': APPENDIX_TOKENS.cellInk,
  '--apx-photo-label-gap': `${APPENDIX_TOKENS.photoLabelGapPt}pt`,
  '--apx-photo-label-size': `${APPENDIX_TOKENS.photoLabelSizePt}pt`,
  '--apx-photo-label-line': `${APPENDIX_TOKENS.photoLabelLineHeight}`,
  '--apx-marker-fill': APPENDIX_TOKENS.markerFill,
  '--apx-marker-stroke': APPENDIX_TOKENS.markerStroke,
  '--apx-marker-ink': APPENDIX_TOKENS.markerInk,
} as unknown as CSSProperties;

export const mmToPt = (mm: number): number => mm * 2.83465;
